import React, { useCallback, useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

// Linking videos one at a time does not work on this data: 94% of the
// unattached videos never name a store in their title, so there is nothing to
// match on. What they do have is a channel, and a channel films at the same
// arcade again and again — twelve decisions here cover three hundred videos.

export default function VideoLinking() {
  useAuthGuard();

  const [channels, setChannels] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [c, s] = await Promise.all([
        apiFetch(`${API_BASE_URL}/admin/video-channels?limit=60`),
        apiFetch(`${API_BASE_URL}/admin/video-suggestions?limit=100`),
      ]);
      if (!c.ok || !s.ok) throw new Error(`HTTP ${c.ok ? s.status : c.status}`);
      setChannels((await c.json()).data || []);
      setSuggestions((await s.json()).data || []);
    } catch (e) {
      setError(e.message || 'Network Error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const linkChannel = async (channel, storeId) => {
    setSaving(channel.channelId);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/video-channels/${encodeURIComponent(channel.channelId)}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      await load();
      alert(`${body.data.linked}本を紐付けました`);
    } catch (e) {
      alert('紐付けに失敗しました: ' + e.message);
    } finally {
      setSaving('');
    }
  };

  const linkOne = async (s) => {
    setSaving(s.id);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/videos/${s.id}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: s.storeId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      alert('紐付けに失敗しました: ' + e.message);
    } finally {
      setSaving('');
    }
  };

  const pending = (channels || []).reduce((n, c) => n + c.unlinked, 0);

  return (
    <Layout>
      <div className="mb-3">
        <h2 className="page-title">動画の店舗紐付け</h2>
        <p className="page-subtitle">
          チャンネル単位でまとめて紐付けます。動画の94%はタイトルに店名が入っていないため、
          1本ずつでは終わりません。1チャンネル決めれば数十本が片付きます。
        </p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {channels === null && <div className="card"><div className="card-body text-center py-5">読み込み中...</div></div>}

      {channels !== null && (
        <>
          <div className="card mb-3">
            <div className="card-body py-2 px-3">
              未紐付け <strong>{pending}</strong> 本 / チャンネル <strong>{channels.length}</strong>
            </div>
          </div>

          {channels.map((c) => (
            <ChannelRow
              key={c.channelId}
              channel={c}
              saving={saving === c.channelId}
              onLink={(storeId) => linkChannel(c, storeId)}
            />
          ))}
        </>
      )}

      {suggestions !== null && suggestions.length > 0 && (
        <>
          <h5 className="mt-4">タイトルに店名がある動画（{suggestions.length}件）</h5>
          <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
            タイトルがちょうど1店舗を名指ししているものだけを候補として出しています。
            自動では紐付けません。
          </p>
          {suggestions.map((s) => (
            <div className="card mb-2" key={s.id}>
              <div className="card-body py-2 px-3 d-flex flex-wrap align-items-center gap-2">
                <span style={{ flex: '1 1 380px', fontSize: '0.8125rem' }}>
                  <a href={`https://www.youtube.com/watch?v=${s.videoId}`} target="_blank" rel="noreferrer">
                    {s.title}
                  </a>
                  <span className="text-muted"> — {s.channelTitle}</span>
                </span>
                <span className="badge bg-primary-subtle text-primary-emphasis">{s.storeName}</span>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={saving === s.id}
                  onClick={() => linkOne(s)}
                >
                  この店舗にする
                </button>
              </div>
            </div>
          ))}
        </>
      )}
    </Layout>
  );
}

/**
 * One channel, with a store picker.
 *
 * A channel whose already-linked videos all point at one store arrives with
 * that store filled in: somebody decided it once, and asking again wastes the
 * decision.
 */
/** YouTubeの視聴URL。動画IDしか持っていないので組み立てる。 */
function watchUrl(videoId) {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
}

function ChannelRow({ channel, saving, onLink }) {
  const samples = channel.samples || [];
  const newest = samples[0];
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores?q=${encodeURIComponent(query)}&limit=8`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setResults(body.data || []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="card mb-2">
      <div className="card-body py-2 px-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          {/* どの店で撮っているかは、見ないと決まらない。サムネイルと
              タイトルから動画を開けるようにする（別タブ）。 */}
          {channel.thumbnail && newest ? (
            <a href={watchUrl(newest.videoId)} target="_blank" rel="noreferrer" title={newest.title}>
              <img
                src={channel.thumbnail}
                alt=""
                style={{ width: 72, height: 40, objectFit: 'cover', borderRadius: 4 }}
              />
            </a>
          ) : (
            channel.thumbnail && (
              <img src={channel.thumbnail} alt="" style={{ width: 72, height: 40, objectFit: 'cover', borderRadius: 4 }} />
            )
          )}
          <span style={{ flex: '1 1 260px' }}>
            <strong>{channel.channelTitle}</strong>
            <span className="text-muted ms-2" style={{ fontSize: '0.8125rem' }}>
              未紐付け {channel.unlinked} / 全{channel.total}本
            </span>
            {samples.length > 0 && (
              <span className="d-block text-truncate" style={{ fontSize: '0.75rem' }}>
                {samples.map((v, i) => (
                  <span key={v.videoId}>
                    {i > 0 && <span className="text-muted"> ・ </span>}
                    <a href={watchUrl(v.videoId)} target="_blank" rel="noreferrer" title={v.title}>
                      {i === 0 ? '最新の動画を見る' : `動画${i + 1}`}
                    </a>
                  </span>
                ))}
              </span>
            )}
          </span>

          {channel.storeName ? (
            <>
              <span className="badge bg-success-subtle text-success-emphasis">
                既存の紐付け: {channel.storeName}
              </span>
              <button
                className="btn btn-success btn-sm"
                disabled={saving}
                onClick={() => onLink(channel.storeId)}
              >
                残り{channel.unlinked}本も同じ店舗にする
              </button>
            </>
          ) : (
            <form className="d-flex gap-2" onSubmit={search}>
              <input
                className="form-control form-control-sm"
                style={{ width: 200 }}
                placeholder="店舗名で検索"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button className="btn btn-phoenix-secondary btn-sm" type="submit" disabled={searching}>
                検索
              </button>
            </form>
          )}
        </div>

        {results.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mt-2">
            {results.map((s) => (
              <button
                key={s.id}
                className="btn btn-outline-primary btn-sm"
                disabled={saving}
                onClick={() => onLink(s.id)}
              >
                {s.name}
                <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>{s.prefecture}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
